/**
 * Alfie: a live voice call in the browser. WebRTC carries the caller's microphone to OpenAI's
 * Realtime API and plays the voice back; the server only brokers the offer (see
 * apps/api/src/alfie-realtime.ts). The shared controller turns the data channel into call state.
 */
import type { RealtimeControllerDeps } from "@rakazo/core";
import { createRealtimeController } from "@rakazo/core";
import { selectedSpaceId, withSpaceHeaders } from "./rpc.js";

export type LiveCall = {
  greet: (opening?: string) => void;
  setMuted: (muted: boolean) => void;
  close: () => void;
};

const CHANNEL_OPEN_TIMEOUT_MS = 15_000;

export function liveCallsSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof RTCPeerConnection !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

/**
 * Connects a live call to the bot. Resolves null when this server has no live calls, so the
 * caller falls back to the device call loop; throws when the call could not connect.
 */
export async function openLiveCall(
  botId: string,
  handlers: Omit<RealtimeControllerDeps, "send">,
  signal: AbortSignal,
): Promise<LiveCall | null> {
  if (!liveCallsSupported()) return null;
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  const pc = new RTCPeerConnection();
  const audio = new Audio();
  audio.autoplay = true;
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    controller?.close();
    try {
      channel.close();
    } catch {
      // Already closed.
    }
    pc.close();
    for (const track of stream.getTracks()) track.stop();
    audio.pause();
    audio.srcObject = null;
  };
  let controller: ReturnType<typeof createRealtimeController> | null = null;
  pc.ontrack = (event) => {
    audio.srcObject = event.streams[0] ?? new MediaStream([event.track]);
    void audio.play().catch(() => undefined);
  };
  for (const track of stream.getAudioTracks()) pc.addTrack(track, stream);
  const channel = pc.createDataChannel("oai-events");
  signal.addEventListener("abort", close, { once: true });
  try {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const res = await fetch("/api/alfie/realtime/call", {
      method: "POST",
      headers: withSpaceHeaders({ "content-type": "application/json" }, selectedSpaceId()),
      credentials: "include",
      body: JSON.stringify({ botId, sdp: offer.sdp }),
      signal,
    });
    const body = (await res.json().catch(() => ({}))) as {
      sdp?: string;
      error?: string;
      code?: string;
    };
    if (res.status === 404 && body.code === "live_calls_off") {
      close();
      return null;
    }
    if (!res.ok || !body.sdp) throw new Error(body.error || "Could not start the call.");
    await pc.setRemoteDescription({ type: "answer", sdp: body.sdp });
    await new Promise<void>((resolve, reject) => {
      if (channel.readyState === "open") return resolve();
      const timer = setTimeout(
        () => reject(new Error("The call did not connect.")),
        CHANNEL_OPEN_TIMEOUT_MS,
      );
      channel.addEventListener(
        "open",
        () => {
          clearTimeout(timer);
          resolve();
        },
        { once: true },
      );
      signal.addEventListener("abort", () => clearTimeout(timer), { once: true });
    });
  } catch (error) {
    close();
    throw error;
  }
  const live = createRealtimeController({
    ...handlers,
    send: (event) => {
      if (channel.readyState === "open") channel.send(JSON.stringify(event));
    },
  });
  controller = live;
  channel.addEventListener("message", (event) => live.handleEvent(event.data));
  pc.addEventListener("connectionstatechange", () => {
    if (!closed && (pc.connectionState === "failed" || pc.connectionState === "closed")) {
      handlers.onError("The call dropped.");
    }
  });
  return {
    greet: (opening) => live.greet(opening),
    setMuted: (muted) => {
      for (const track of stream.getAudioTracks()) track.enabled = !muted;
    },
    close,
  };
}
